import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { createLogger } from "../utils/loggingUtils";
import { 
  getCurrentSession, 
  updateCurrentSession, 
  resetSessionState, 
  trackRefreshAttempt, 
  shouldRateLimitRefresh, 
  getRefreshAttemptsState 
} from "./sessionState";
import { checkSessionStatus, isSessionExpiringSoon } from "./sessionValidator";

const logger = createLogger('sessionUtils');

// Keep track of ongoing refresh to prevent parallel refreshes
let refreshInProgress = false;

// Export imported functions to maintain API compatibility
export { 
  resetSessionState,
  checkSessionStatus
};

// Ensure we have a valid session before making API calls
export const ensureValidSession = async (): Promise<Session | null> => {
  logger.debug('Validating session...');
  
  try {
    // If we already have a session, check if it's still valid
    const currentSession = getCurrentSession();
    if (currentSession) {
      const expiresAt = currentSession.expires_at;
      if (expiresAt) {
        const now = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = expiresAt - now;
        
        // Only refresh if session expires very soon (less than 2 minutes)
        if (timeUntilExpiry < 120) {
          logger.debug(`Session expiring soon (${timeUntilExpiry}s), refreshing`);
          return await forceSessionRefresh();
        }
        
        // Session is still valid
        logger.debug(`Using existing session (expires in ${timeUntilExpiry}s)`);
        return currentSession;
      }
    }
    
    // Otherwise, get the current session
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      logger.error('Error getting session:', error);
      return null;
    }
    
    if (!data.session) {
      logger.debug('No active session found'); // Changed from warn to debug
      return null;
    }
    
    // Check if session expires soon and refresh if needed
    if (await isSessionExpiringSoon(120)) { // 2 minute threshold 
      logger.debug(`Session expiring soon, refreshing`);
      return await forceSessionRefresh();
    }
    
    // Update the current session
    updateCurrentSession(data.session);
    return data.session;
  } catch (error) {
    logger.error('Exception in ensureValidSession:', error);
    
    // Try to recover with a force refresh
    try {
      logger.debug('Attempting to recover with forced refresh');
      return await forceSessionRefresh();
    } catch (refreshError) {
      logger.error('Recovery attempt failed:', refreshError);
      return null;
    }
  }
};

// Force a session refresh with debouncing to prevent parallel refreshes
export const forceSessionRefresh = async (): Promise<Session | null> => {
  // Prevent parallel refresh attempts
  if (refreshInProgress) {
    logger.debug('Refresh already in progress, waiting...');
    
    // Wait for current refresh to complete (max 5 seconds)
    let attempts = 0;
    while (refreshInProgress && attempts < 10) {
      await new Promise(resolve => setTimeout(resolve, 500));
      attempts++;
    }
    
    // If refresh is still in progress after timeout, use existing session
    if (refreshInProgress) {
      logger.debug('Refresh still in progress after timeout, using existing session');
      return getCurrentSession();
    }
    
    return getCurrentSession();
  }
  
  // Apply rate limiting to prevent excessive refresh attempts
  if (shouldRateLimitRefresh()) {
    logger.debug('Refresh attempt too soon, using existing session');
    return getCurrentSession();
  }
  
  // Get refresh attempts state
  const refreshState = getRefreshAttemptsState();
  
  // Apply exponential backoff if we've had many attempts
  if (refreshState.shouldApplyBackoff) {
    const backoffMs = refreshState.getBackoffTime();
    logger.debug(`Too many refresh attempts (${refreshState.count}), applying backoff: ${backoffMs}ms`);
    
    // We'll still try, but with a delay
    await new Promise(resolve => setTimeout(resolve, backoffMs));
  }
  
  logger.debug('Starting session refresh...');
  refreshInProgress = true;
  
  try {
    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) {
      logger.error('Error refreshing session:', error);
      trackRefreshAttempt(false);
      
      // If we get an invalid refresh token error, try to get a fresh session
      if (error.message.includes('refresh token is invalid')) {
        logger.debug('Refresh token invalid, trying to get fresh session');
        const { data: freshData, error: freshError } = await supabase.auth.getSession();
        
        if (freshError) {
          logger.error('Error getting fresh session:', freshError);
          refreshInProgress = false;
          return null;
        }
        
        if (freshData.session) {
          logger.debug('Successfully got fresh session');
          updateCurrentSession(freshData.session);
          trackRefreshAttempt(true);
          refreshInProgress = false;
          return freshData.session;
        }
      }
      
      refreshInProgress = false;
      return null;
    }
    
    if (!data.session) {
      logger.debug('No session returned after refresh'); // Changed from warn to debug
      trackRefreshAttempt(false);
      refreshInProgress = false;
      return null;
    }
    
    // Update the current session
    updateCurrentSession(data.session);
    trackRefreshAttempt(true);
    logger.debug('Session refreshed successfully');
    
    refreshInProgress = false;
    return data.session;
  } catch (error) {
    logger.error('Exception in forceSessionRefresh:', error);
    trackRefreshAttempt(false);
    
    // Last resort: try to get a fresh session
    try {
      logger.debug('Exception occurred, trying to get fresh session');
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        logger.error('Error getting fresh session:', error);
        refreshInProgress = false;
        return null;
      }
      
      if (data.session) {
        logger.debug('Successfully got fresh session after exception');
        updateCurrentSession(data.session);
        trackRefreshAttempt(true);
        refreshInProgress = false;
        return data.session;
      }
      
      refreshInProgress = false;
      return null;
    } catch (secondError) {
      logger.error('Second exception in recovery attempt:', secondError);
      refreshInProgress = false;
      return null;
    }
  }
};
